import { useCallback } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { reverse } from "utils/urls";

import Text from "components/Text";
import toast from "components/Toast";

import { useOpenAIPromptResponseReady } from "entities/AISummaries/hooks";
import {
  useBulkDealImportReady,
  useDealCompanyImportTableSheetNameNotFound,
  useDealCompanyImportTableUpdateFailed,
  useDealTableUpdateSucceeded,
  useTableOrDealTableUpdateFailed,
  useTableResponseReady,
  useTableUpdateSucceeded,
} from "entities/TableStorage/hooks";

const GlobalNotifications = () => {
  const navigate = useNavigate();

  // openAI prompt - response
  const openAIPromptResponseReadyEventHandler = useCallback(
    ({
      dealId,
      openAIDocumentAssistantId,
      promptResponseId,
    }: {
      dealId: number;
      openAIDocumentAssistantId: number;
      promptResponseId: number;
    }) => {
      toast.successMessage(
        <Text variant="text2">
          Prompt response has been generated successfully! Click{" "}
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                `${reverse(URLS.DEAL_AI_SUMMARIES_EDITOR, {
                  dealId,
                  openAIDocumentAssistantId,
                })}?prompt-response-id=${promptResponseId}`
              )
            }
          >
            here
          </Text>{" "}
          to view the answer.
        </Text>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  useOpenAIPromptResponseReady({
    eventHandler: openAIPromptResponseReadyEventHandler,
  });

  const tableResponseSuccessEventHandler = useCallback(
    ({ tableResponseId }: { tableResponseId: number }) => {
      toast.successMessage(
        <Text variant="text2">
          Table report has been generated successfully! Click{" "}
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                reverse(URLS.TABLES_RESPONSE_DETAIL, {
                  tableResponseId,
                })
              )
            }
          >
            here
          </Text>{" "}
          to view the report.
        </Text>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  const tableResponseFailedEventHandler = useCallback(
    () => toast.errorMessage("Table report failed. Please try again."),
    []
  );

  useTableResponseReady({
    successEventHandler: tableResponseSuccessEventHandler,
    failureEventHandler: tableResponseFailedEventHandler,
  });

  const bulkDealImportSuccessEventHandler = useCallback(() => {
    toast.successMessage(
      <Text variant="text2">
        All deals have been imported successfully! Click{" "}
        <Text
          component="span"
          variant="text2"
          color="primary"
          onClick={() =>
            navigate(
              reverse(URLS.PROJECTS_LIST, {
                phase: "All",
              })
            )
          }
        >
          here
        </Text>{" "}
        to view the deal dashboard.
      </Text>,
      { autoClose: false }
    );
  }, [navigate]);

  const bulkDealImportFailedEventHandler = useCallback(
    ({ detail }: { detail: string | Record<string, string[]> }) => {
      if (typeof detail === "string") {
        // Handle plain string errors
        toast.errorMessage(detail, {
          autoClose: false,
        });
      } else if (typeof detail === "object" && !_.isNil(detail)) {
        // Handle JSON object errors
        Object.entries(detail).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach((error) => {
              toast.errorMessage(
                `An error occured in ${field.replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase())}: ${error}`,
                {
                  autoClose: false,
                }
              );
            });
          } else {
            toast.errorMessage(`An error occured in ${field}: ${errors}`, {
              autoClose: false,
            });
          }
        });
      } else {
        toast.errorMessage("An unexpected error occurred.", {
          autoClose: false,
        });
      }
    },
    []
  );

  useBulkDealImportReady({
    successEventHandler: bulkDealImportSuccessEventHandler,
    failureEventHandler: bulkDealImportFailedEventHandler,
  });

  const tableOrDealTableUpdateFailedEventHandler = useCallback(
    ({ details }) => {
      return toast.errorMessage(details, { autoClose: false });
    },
    []
  );

  const dealTableUpdateSucceededEventHandler = useCallback(
    ({ dealId, tableId, tableName }) => {
      toast.successMessage(
        <>
          <Text variant="text2" component="span">
            Table "{tableName}" has been updated successfully! Click{" "}
          </Text>
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                reverse(URLS.DEAL_TABLE_STORAGE_DETAILS, {
                  dealId,
                  tableId,
                })
              )
            }
          >
            here
          </Text>{" "}
          <Text variant="text2" component="span">
            to view the changes.
          </Text>
        </>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  useDealTableUpdateSucceeded({
    successEventHandler: dealTableUpdateSucceededEventHandler,
  });

  const tableUpdateSucceededEventHandler = useCallback(
    ({ tableId, tableName }) => {
      toast.successMessage(
        <>
          <Text variant="text2" component="span">
            Table "{tableName}" has been updated successfully! Click{" "}
          </Text>
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                reverse(URLS.TABLE_STORAGE_DETAILS, {
                  tableId,
                })
              )
            }
          >
            here
          </Text>{" "}
          <Text variant="text2" component="span">
            to view the changes.
          </Text>
        </>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  useTableUpdateSucceeded({
    successEventHandler: tableUpdateSucceededEventHandler,
  });

  useTableOrDealTableUpdateFailed({
    failureEventHandler: tableOrDealTableUpdateFailedEventHandler,
  });

  useDealCompanyImportTableSheetNameNotFound({
    eventHandler: ({ details }) =>
      toast.loading(details, {
        type: "info",
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        isLoading: false,
        closeButton: true,
      }),
  });

  useDealCompanyImportTableUpdateFailed({
    eventHandler: ({ details }) =>
      toast.errorMessage(details, { autoClose: false }),
  });

  return null;
};

export default GlobalNotifications;
